/**
 * Wrapped textarea with label and error display.
 *
 * Props:
 *   id             — ties the <label> htmlFor to the <textarea> id (required for a11y)
 *   label          — label text; omit to skip the label element
 *   error          — error string; renders below the textarea when truthy
 *   rows           — visible row count (default: 3)
 *   className      — extra class(es) on the wrapping <div>
 *   textareaClassName — extra class(es) on the <textarea> element itself (e.g. a
 *                   layout-specific sizing class); opt-in, empty by default
 *
 * All other props (value, onChange, placeholder, disabled, ...) pass to <textarea>.
 */
import { forwardRef } from 'react';

const Textarea = forwardRef(function Textarea(
  { id, label, error, rows = 3, className = '', textareaClassName = '', ...props },
  ref
) {
  return (
    <div className={['form-group', className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <textarea
        id={id}
        ref={ref}
        rows={rows}
        className={['form-input', error ? 'form-input--error' : '', textareaClassName].filter(Boolean).join(' ')}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...props}
      />
      {error && (
        <p id={`${id}-error`} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

export default Textarea;
